import React, { useEffect, useState } from "react";
import { ExternalLink, X } from "lucide-react";

const NO_EMBED = /(^|\.)(instagram|facebook|fb|tiktok|twitter|x|linkedin|pinterest|yelp|google|youtube|threads)\.(com|net)$/i;

// Opens links clicked inside the CRM/dialer in a slide-out panel instead of a new tab.
export function LinkDrawer() {
  const [url, setUrl] = useState("");
  useEffect(() => {
    const onClick = (e) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey) return;
      const a = e.target.closest?.("a[href]");
      if (!a || a.closest("[data-link-drawer]") || a.hasAttribute("download")) return;
      const href = a.href;
      if (!/^https?:/i.test(href) || (a.target !== "_blank" && new URL(href).origin === window.location.origin)) return;
      e.preventDefault();
      setUrl(href);
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);
  useEffect(() => {
    if (!url) return undefined;
    const k = (e) => { if (e.key === "Escape") { e.stopImmediatePropagation(); e.preventDefault(); setUrl(""); } };
    window.addEventListener("keydown", k, true);
    return () => window.removeEventListener("keydown", k, true);
  }, [url]);
  if (!url) return null;
  const host = new URL(url).hostname.replace(/^www\./, "");
  return (
    <div className="fixed inset-0 z-[150]" data-link-drawer data-testid="link-drawer">
      <button className="absolute inset-0 bg-black/30" onClick={() => setUrl("")} aria-label="Close preview" data-testid="link-drawer-backdrop" />
      <aside className="absolute right-0 top-0 h-full w-full md:w-[min(900px,70vw)] bg-white shadow-2xl flex flex-col animate-[slideIn_.25s_ease-out]" style={{ animationName: "lifSlideIn" }}>
        <style>{"@keyframes lifSlideIn{from{transform:translateX(100%)}to{transform:none}}"}</style>
        <div className="flex items-center gap-2 px-4 py-3 border-b border-[var(--line)]">
          <p className="flex-1 truncate text-sm font-medium" data-testid="link-drawer-url">{url.replace(/^https?:\/\//, "")}</p>
          <a href={url} target="_blank" rel="noreferrer" className="btn-outline-gold !py-1.5 text-xs" data-testid="link-drawer-open-browser"><ExternalLink className="w-3.5 h-3.5" />Open in browser</a>
          <button onClick={() => setUrl("")} className="rounded-full p-2 hover:bg-black/5" aria-label="Close" data-testid="link-drawer-close"><X className="w-5 h-5" /></button>
        </div>
        {NO_EMBED.test(host) ? <div className="flex-1 flex flex-col items-center justify-center gap-4 p-8 text-center bg-[var(--bg)]" data-testid="link-drawer-noembed">
          <p className="font-serif text-2xl">{host}</p>
          <p className="text-sm text-[#5c544b] max-w-sm">This site doesn't allow previews inside other pages.</p>
          <a href={url} target="_blank" rel="noreferrer" className="btn-outline-gold" data-testid="link-drawer-noembed-open"><ExternalLink className="w-4 h-4" />Open {host}</a>
        </div> : <iframe title="Link preview" src={url} className="flex-1 w-full" sandbox="allow-scripts allow-same-origin allow-forms allow-popups" data-testid="link-drawer-frame" />}
        <p className="text-[11px] text-[#5c544b] px-4 py-2 border-t border-[var(--line)]">Blank page? Some sites don't allow previews. Use "Open in browser".</p>
      </aside>
    </div>
  );
}
